/**
 * Сервис сопоставления кастомных полей с AMO полями
 * Обеспечивает поиск соответствий по названию и типу
 */

import { getAllAmoFields, normalizeFieldName } from '../utils/amoFieldsUtils';


if (typeof window !== 'undefined') {
    window.debugFieldSearch = null; 
}

/**
 * Проверка совместимости типов полей
 * @param {string} customFieldType - Тип кастомного поля
 * @param {string} amoFieldType - Тип AMO поля (TEXT, SELECT, MULTISELECT, etc.)
 * @returns {boolean} Совместимы ли типы
 */
export function areFieldTypesCompatible(customFieldType, amoFieldType) {
    const compatibilityMap = {
        
        'C': ['CHECKBOX', 'SELECT', 'TEXT'], 
        'S': ['SELECT', 'MULTISELECT', 'TEXT'],              
        'M': ['MULTISELECT', 'TEXT'],
        
        
        'selector': ['SELECT', 'MULTISELECT', 'TEXT'],
        'multiselect': ['MULTISELECT', 'TEXT'], 
        'text': ['TEXT', 'TEXTAREA', 'SELECT', 'MULTISELECT'],
        'checkbox': ['CHECKBOX', 'SELECT', 'TEXT']
    };
    
    
    const normalizedAmoType = amoFieldType?.toUpperCase();
    
    return compatibilityMap[customFieldType]?.includes(normalizedAmoType) || false;
}

/**
 * Отладочная функция для диагностики поиска полей
 * @param {string} customFieldName - Название кастомного поля
 * @param {string} customFieldType - Тип кастомного поля
 * @returns {Object} Отладочная информация
 */
export function debugFieldSearch(customFieldName, customFieldType) {
    const fields = getAllAmoFields();
    const normalizedCustomName = normalizeFieldName(customFieldName);
    
    const debug = {
        customFieldName,
        customFieldType,
        normalizedCustomName,
        amoFieldsTotal: fields.length,
        foundFields: [],
        typeCompatibility: {},
        exactMatches: [],
        partialMatches: [],
        containsMatches: []
    };
    
    
    fields.forEach(amoField => {
        if (amoField.normalizedName.includes(normalizedCustomName) || 
            normalizedCustomName.includes(amoField.normalizedName)) {
            debug.foundFields.push({
                id: amoField.id,
                name: amoField.name,
                normalizedName: amoField.normalizedName,
                type: amoField.type,
                compatible: areFieldTypesCompatible(customFieldType, amoField.type)
            });
        }
        
        
        if (amoField.normalizedName === normalizedCustomName) {
            debug.typeCompatibility[amoField.type] = areFieldTypesCompatible(customFieldType, amoField.type);
        }
    });
    
    
    debug.exactMatches = fields.filter(amoField => 
        amoField.normalizedName === normalizedCustomName
    ).map(field => ({
        ...field,
        compatible: areFieldTypesCompatible(customFieldType, field.type)
    }));
    
    console.log('Отладка поиска поля:', debug);
    return debug;
}


if (typeof window !== 'undefined') {
    window.debugFieldSearch = debugFieldSearch;
}

/**
 * Поиск AMO поля по названию
 * @param {string} customFieldName - Название кастомного поля
 * @param {string} customFieldType - Тип кастомного поля
 * @param {Array} amoFields - Массив AMO полей (опционально, иначе получает автоматически)
 * @returns {Object|null} Найденное AMO поле или null
 */
export function findAmoFieldByName(customFieldName, customFieldType, amoFields = null) {
    try {
        const fields = amoFields || getAllAmoFields();
        const normalizedCustomName = normalizeFieldName(customFieldName);
        
        if (!normalizedCustomName) {
            console.warn('Не удалось нормализовать название поля:', customFieldName);
            return null;
        }
        
        
        const exactMatch = fields.find(amoField => 
            amoField.normalizedName === normalizedCustomName &&
            areFieldTypesCompatible(customFieldType, amoField.type)
        );
        
        if (exactMatch) {
            return exactMatch;
        }
        
        
        
        const nameMatch = fields.find(amoField => 
            amoField.normalizedName === normalizedCustomName
        );
        
        if (nameMatch) {
            console.warn(`Найдено поле с совпадающим именем "${customFieldName}", но несовместимые типы: ${customFieldType} vs ${nameMatch.type}`);
        }
        
        console.warn(`AMO поле не найдено для: "${customFieldName}" (${customFieldType})`);
        return null;
    } catch (error) {
        console.error('Ошибка при поиске AMO поля по названию:', error);
        return null;
    }
}

/**
 * Получение всех сопоставлений полей для чек-листа
 * @param {Array} customFields - Массив кастомных полей чек-листа
 * @returns {Map} Карта сопоставлений {customFieldId: amoField}
 */
export function getFieldMappings(customFields) {
    try {
        const mappings = new Map();
        const amoFields = getAllAmoFields();
        
        if (!Array.isArray(customFields) || amoFields.length === 0) {
            return mappings;
        }
        
        customFields.forEach(customField => {
            
            if (customField.type === 'C') {
                return;
            }
            
            const amoField = findAmoFieldByName(
                customField.name, 
                customField.type, 
                amoFields
            );
            
            if (amoField) {
                mappings.set(customField.id, {
                    customField,
                    amoField,
                    compatible: areFieldTypesCompatible(customField.type, amoField.type)
                });
            }
        });
        
        return mappings;
    } catch (error) {
        console.error('Ошибка при создании карты сопоставлений:', error);
        return new Map();
    }
}

/**
 * Получение сопоставления для конкретного кастомного поля
 * @param {Object} customField - Кастомное поле
 * @returns {Object|null} Сопоставление или null
 */
export function getFieldMapping(customField) {
    try {
        if (!customField || customField.type === 'C') {
            return null;
        }
        
        const amoField = findAmoFieldByName(customField.name, customField.type);
        
        if (!amoField) {
            return null;
        }
        
        return {
            customField,
            amoField,
            compatible: areFieldTypesCompatible(customField.type, amoField.type)
        };
    } catch (error) {
        console.error('Ошибка при получении сопоставления поля:', error);
        return null;
    }
}

/**
 * Проверка наличия сопоставления для поля
 * @param {Object} customField - Кастомное поле
 * @returns {boolean} Есть ли сопоставление
 */
export function hasFieldMapping(customField) {
    return getFieldMapping(customField) !== null;
}

/**
 * Получение статистики сопоставлений для чек-листа
 * @param {Array} customFields - Массив кастомных полей
 * @returns {Object} Статистика сопоставлений
 */
export function getMappingStats(customFields) {
    try {
        const mappings = getFieldMappings(customFields);
        const syncableFields = customFields.filter(cf => cf.type !== 'C');
        
        return {
            total: syncableFields.length,
            mapped: mappings.size,
            unmapped: syncableFields.length - mappings.size,
            coverage: syncableFields.length > 0 ? Math.round((mappings.size / syncableFields.length) * 100) : 0
        };
    } catch (error) {
        console.error('Ошибка при получении статистики сопоставлений:', error);
        return {
            total: 0,
            mapped: 0,
            unmapped: 0,
            coverage: 0
        };
    }
} 